import React from 'react';
import { getColor, getCycleColor } from './colorUtils';
import type { ColorSet } from './colorUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HierarchyNode {
  label: string;
  description?: string;
  color?: string;
  children?: HierarchyNode[];
}

interface HierarchyMapProps {
  title?: string;
  root: HierarchyNode;
  direction?: 'top-down' | 'left-right';
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute the maximum width (number of nodes) at any single level of the tree. */
function maxBreadth(node: HierarchyNode): number {
  if (!node.children || node.children.length === 0) return 1;
  const childWidths = node.children.map(maxBreadth);
  const thisLevelWidth = node.children.length;
  const deeperMax = Math.max(...childWidths, 0);
  return Math.max(thisLevelWidth, deeperMax);
}

/** Resolve a ColorSet for a node given its depth in the tree. */
function resolveColor(node: HierarchyNode, depth: number): ColorSet {
  return node.color ? getColor(node.color) : getCycleColor(depth);
}

// ---------------------------------------------------------------------------
// Node box
// ---------------------------------------------------------------------------

const NodeBox: React.FC<{
  node: HierarchyNode;
  depth: number;
  compact: boolean;
}> = ({ node, depth, compact }) => {
  const colorSet = resolveColor(node, depth);
  const px = compact ? 'px-3 py-1.5' : 'px-4 py-2';
  const labelSize = compact ? 'text-xs' : 'text-sm';
  const descSize = compact ? 'text-[10px]' : 'text-xs';

  return (
    <div
      className={[
        'rounded-lg shadow-sm text-center whitespace-nowrap',
        px,
        colorSet.bg,
      ].join(' ')}
    >
      <div className={`font-semibold text-white leading-tight ${labelSize}`}>
        {node.label}
      </div>
      {node.description && (
        <div
          className={`text-white/80 leading-snug mt-0.5 ${descSize} whitespace-normal max-w-[12rem]`}
        >
          {node.description}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tree connector (top-down)
//
// Draws:
//   1. A short vertical line down from the parent center
//   2. A horizontal rail spanning from the first to last child center
//   3. A short vertical line down to each child center
//
// We achieve this with a simple CSS grid/flex approach:
//   - The children row is a flex row with equal spacing.
//   - Above it, we render a connector row with the same flex layout.
//   - Each connector slot has a vertical stub going up to the rail.
//   - The rail is an absolutely-positioned horizontal line that spans the
//     center of the first slot to the center of the last slot.
// ---------------------------------------------------------------------------

const CONNECTOR_H = 24; // height of the connector zone in px
const CONNECTOR_H_COMPACT = 16;

/** Vertical stub from parent center down to the rail. */
const ParentStub: React.FC<{ h: number }> = ({ h }) => (
  <div className="flex justify-center" style={{ height: h }}>
    <div className="w-0.5 bg-gray-300 h-full" />
  </div>
);

/** The connector zone between a parent node and its children row (top-down). */
const TopDownConnector: React.FC<{
  childCount: number;
  compact: boolean;
}> = ({ childCount, compact }) => {
  const h = compact ? CONNECTOR_H_COMPACT : CONNECTOR_H;

  if (childCount === 0) return null;

  if (childCount === 1) {
    // Single child: just a straight vertical line
    return <ParentStub h={h * 2} />;
  }

  return (
    <div style={{ height: h * 2 }}>
      {/* Top half: vertical line down from parent center */}
      <ParentStub h={h} />
      {/* Bottom half: horizontal rail + vertical stubs to each child */}
      <div className="relative" style={{ height: h }}>
        {/* Horizontal rail: spans from first to last child center.
            We use left 50%/first-child and right 50%/last-child trick.
            Simpler: the rail spans the full width minus half a child-slot
            on each end. We do this with inset percentages. */}
        <div
          className="absolute bg-gray-300"
          style={{
            height: 2,
            top: 0,
            // Span from center of first slot to center of last slot
            left: `${100 / (2 * childCount)}%`,
            right: `${100 / (2 * childCount)}%`,
          }}
        />
        {/* Vertical stubs from rail down to each child */}
        <div className="flex h-full">
          {Array.from({ length: childCount }).map((_, i) => (
            <div key={i} className="flex-1 flex justify-center">
              <div className="w-0.5 bg-gray-300 h-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tree connector (left-right)
// ---------------------------------------------------------------------------

const LEFT_RIGHT_CONNECTOR_W = 24;
const LEFT_RIGHT_CONNECTOR_W_COMPACT = 16;

/** The connector zone between a parent node and its children column (left-right). */
const LeftRightConnector: React.FC<{
  childCount: number;
  compact: boolean;
}> = ({ childCount, compact }) => {
  const w = compact ? LEFT_RIGHT_CONNECTOR_W_COMPACT : LEFT_RIGHT_CONNECTOR_W;

  if (childCount === 0) return null;

  if (childCount === 1) {
    return (
      <div className="flex items-center" style={{ width: w * 2 }}>
        <div className="h-0.5 bg-gray-300 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-row" style={{ width: w * 2 }}>
      {/* Left half: horizontal line from parent center */}
      <div className="flex items-center" style={{ width: w }}>
        <div className="h-0.5 bg-gray-300 w-full" />
      </div>
      {/* Right half: vertical rail + horizontal stubs to each child */}
      <div className="relative" style={{ width: w }}>
        {/* Vertical rail */}
        <div
          className="absolute bg-gray-300"
          style={{
            width: 2,
            left: 0,
            top: `${100 / (2 * childCount)}%`,
            bottom: `${100 / (2 * childCount)}%`,
          }}
        />
        {/* Horizontal stubs from rail to each child */}
        <div className="flex flex-col h-full">
          {Array.from({ length: childCount }).map((_, i) => (
            <div key={i} className="flex-1 flex items-center">
              <div className="h-0.5 bg-gray-300 w-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Recursive subtree renderers
// ---------------------------------------------------------------------------

/** Render a subtree in top-down orientation. */
const SubtreeTopDown: React.FC<{
  node: HierarchyNode;
  depth: number;
  compact: boolean;
}> = ({ node, depth, compact }) => {
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="flex flex-col items-center">
      {/* This node */}
      <NodeBox node={node} depth={depth} compact={compact} />

      {/* Connector + children */}
      {hasChildren && (
        <>
          <TopDownConnector
            childCount={node.children!.length}
            compact={compact}
          />
          <div
            className={[
              'flex flex-row items-start justify-center',
              compact ? 'gap-2' : 'gap-4',
            ].join(' ')}
          >
            {node.children!.map((child, i) => (
              <SubtreeTopDown
                key={i}
                node={child}
                depth={depth + 1}
                compact={compact}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/** Render a subtree in left-right orientation. */
const SubtreeLeftRight: React.FC<{
  node: HierarchyNode;
  depth: number;
  compact: boolean;
}> = ({ node, depth, compact }) => {
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="flex flex-row items-center">
      {/* This node */}
      <NodeBox node={node} depth={depth} compact={compact} />

      {/* Connector + children */}
      {hasChildren && (
        <>
          <LeftRightConnector
            childCount={node.children!.length}
            compact={compact}
          />
          <div
            className={[
              'flex flex-col items-start justify-center',
              compact ? 'gap-1' : 'gap-3',
            ].join(' ')}
          >
            {node.children!.map((child, i) => (
              <SubtreeLeftRight
                key={i}
                node={child}
                depth={depth + 1}
                compact={compact}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Indented list fallback (mobile / wide trees)
// ---------------------------------------------------------------------------

const IndentedNode: React.FC<{
  node: HierarchyNode;
  depth: number;
  compact: boolean;
}> = ({ node, depth, compact }) => {
  const colorSet = resolveColor(node, depth);
  const labelSize = compact ? 'text-xs' : 'text-sm';
  const descSize = compact ? 'text-[10px]' : 'text-xs';
  const indent = depth * (compact ? 16 : 20);

  return (
    <>
      <div className="flex items-start" style={{ paddingLeft: indent }}>
        {/* Color dot */}
        <div
          className={`w-2.5 h-2.5 rounded-full mt-1 mr-2 flex-shrink-0 ${colorSet.bg}`}
        />
        <div>
          <span className={`font-semibold text-gray-900 ${labelSize}`}>
            {node.label}
          </span>
          {node.description && (
            <span className={`text-gray-500 ml-1.5 ${descSize}`}>
              {node.description}
            </span>
          )}
        </div>
      </div>
      {node.children?.map((child, i) => (
        <IndentedNode
          key={i}
          node={child}
          depth={depth + 1}
          compact={compact}
        />
      ))}
    </>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const HierarchyMap: React.FC<HierarchyMapProps> = ({
  title,
  root,
  direction = 'top-down',
  compact = false,
}) => {
  if (!root) return null;

  const breadth = maxBreadth(root);
  const isWide = breadth > 4;

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {/* Optional title header */}
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-800 m-0">
            {title}
          </h3>
        </div>
      )}

      {/* Tree content */}
      <div className={compact ? 'p-4' : 'p-5 md:p-6'}>
        {/* Desktop: graphical tree (hidden on mobile when tree is wide) */}
        <div
          className={[
            'overflow-x-auto',
            isWide ? 'hidden md:block' : '',
          ].join(' ')}
        >
          <div
            className={[
              'inline-flex w-full',
              direction === 'top-down' ? 'justify-center' : 'justify-start',
            ].join(' ')}
          >
            {direction === 'top-down' ? (
              <SubtreeTopDown node={root} depth={0} compact={compact} />
            ) : (
              <SubtreeLeftRight node={root} depth={0} compact={compact} />
            )}
          </div>
        </div>

        {/* Mobile fallback: indented list (shown on mobile when tree is wide) */}
        {isWide && (
          <div
            className={[
              'md:hidden flex flex-col',
              compact ? 'gap-1.5' : 'gap-2',
            ].join(' ')}
          >
            <IndentedNode node={root} depth={0} compact={compact} />
          </div>
        )}
      </div>
    </div>
  );
};

export default HierarchyMap;
